// Kopie von app/src/proeditor/utils/math/cubicBezier.ts (10.10.2026): der geteilte
// Renderer kannte die Easing-Art „custom“ (eigene Kurve im Keyframe-Menü) nicht
// — Easings["custom"] war undefined, jeder solche Keyframe brach auf Bühne und
// im Leser. Gleiche Rechnung wie im Editor, damit beide gleich laufen.

// Cubic-Bézier easing, CSS-style. Control points P1 = (x1, y1) and
// P2 = (x2, y2); the endpoints are fixed at (0, 0) and (1, 1). Returns a
// function mapping progress t ∈ [0, 1] to an eased value (y can overshoot
// [0, 1] when the handles are pulled outside the unit square).
//
// Same numerical approach as the well-known `bezier-easing` package: sample
// the x-curve, then refine with Newton–Raphson (falling back to binary
// subdivision on near-flat slopes).

const NEWTON_ITERATIONS = 4
const NEWTON_MIN_SLOPE = 0.001
const SUBDIVISION_PRECISION = 0.0000001
const SUBDIVISION_MAX_ITERATIONS = 10
const SPLINE_TABLE_SIZE = 11
const SAMPLE_STEP = 1.0 / (SPLINE_TABLE_SIZE - 1.0)

const a = (a1: number, a2: number) => 1.0 - 3.0 * a2 + 3.0 * a1
const b = (a1: number, a2: number) => 3.0 * a2 - 6.0 * a1
const c = (a1: number) => 3.0 * a1

// the polynomial value for a single axis at parameter t
const calc = (t: number, a1: number, a2: number) =>
  ((a(a1, a2) * t + b(a1, a2)) * t + c(a1)) * t

const slope = (t: number, a1: number, a2: number) =>
  3.0 * a(a1, a2) * t * t + 2.0 * b(a1, a2) * t + c(a1)

const binarySubdivide = (
  x: number,
  lower: number,
  upper: number,
  x1: number,
  x2: number
) => {
  let currentX
  let currentT
  let i = 0
  do {
    currentT = lower + (upper - lower) / 2.0
    currentX = calc(currentT, x1, x2) - x
    if (currentX > 0.0) {
      upper = currentT
    } else {
      lower = currentT
    }
  } while (
    Math.abs(currentX) > SUBDIVISION_PRECISION &&
    ++i < SUBDIVISION_MAX_ITERATIONS
  )
  return currentT
}

const newtonRaphson = (x: number, guess: number, x1: number, x2: number) => {
  for (let i = 0; i < NEWTON_ITERATIONS; i += 1) {
    const currentSlope = slope(guess, x1, x2)
    if (currentSlope === 0.0) {
      return guess
    }
    const currentX = calc(guess, x1, x2) - x
    guess -= currentX / currentSlope
  }
  return guess
}

export function cubicBezier(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): (t: number) => number {
  // identity shortcut (linear)
  if (x1 === y1 && x2 === y2) {
    return (t: number) => t
  }

  const sampleValues = new Float32Array(SPLINE_TABLE_SIZE)
  for (let i = 0; i < SPLINE_TABLE_SIZE; i += 1) {
    sampleValues[i] = calc(i * SAMPLE_STEP, x1, x2)
  }

  const tForX = (x: number) => {
    let intervalStart = 0.0
    let current = 1
    const last = SPLINE_TABLE_SIZE - 1
    for (
      ;
      current !== last && sampleValues[current] <= x;
      current += 1
    ) {
      intervalStart += SAMPLE_STEP
    }
    current -= 1
    const dist =
      (x - sampleValues[current]) /
      (sampleValues[current + 1] - sampleValues[current])
    const guess = intervalStart + dist * SAMPLE_STEP
    const initialSlope = slope(guess, x1, x2)
    if (initialSlope >= NEWTON_MIN_SLOPE) {
      return newtonRaphson(x, guess, x1, x2)
    }
    if (initialSlope === 0.0) {
      return guess
    }
    return binarySubdivide(x, intervalStart, intervalStart + SAMPLE_STEP, x1, x2)
  }

  return (t: number) => {
    if (t <= 0) return 0
    if (t >= 1) return 1
    return calc(tForX(t), y1, y2)
  }
}

// A sensible default custom curve (≈ CSS ease-in-out): [x1, y1, x2, y2]
export const DEFAULT_BEZIER_HANDLES: [number, number, number, number] = [
  0.42, 0, 0.58, 1,
]
